import React, { useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { useSearchMoveQuery } from "../store/moves/apis";
import { Input } from "./ui/input"; // Adjust import path as needed
import { formatDateWithOrdinal } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { parseISO } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

type MoveSearchResult = {
  id: number;
  name: string;
  mobile_phone: string;
  alternate_phone: string;
  email: string;
  service_date: string;
  status: string;
};

const MoveSearch: React.FC<{
  placeholder: string;
}> = ({ placeholder }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: moves, isLoading } = useSearchMoveQuery(debouncedSearchTerm, {
    skip: debouncedSearchTerm.length < 3,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsResultsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsResultsOpen(debouncedSearchTerm.length >= 3 && !isLoading && !!moves);
  }, [debouncedSearchTerm, isLoading, moves]);

  const handleSelectMove = (
    event: React.MouseEvent,
    move: MoveSearchResult
  ) => {
    if (!event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      setIsResultsOpen(false);
      setSearchTerm("");
      navigate(`/move/${move.id}`);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="flex items-center h-full gap-1 absolute top-6 left-0 right-0">
        <FontAwesomeIcon icon={faSearch} className="w-3 h-3 text-stone-500" />
        <Input
          type="search"
          placeholder={placeholder}
          className="rounded-md border-none bg-white h-12 focus:outline-none focus:ring-0 focus:border-none focus-visible:border-none focus-visible:ring-0 bg-transparent"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      {isResultsOpen && (
        <ul className="absolute -mt-2 top-12 -left-4 z-[999] w-full mt-0 shadow-lg h-60 overflow-y-auto bg-stone-50 border border-stone-400 divide-y divide-gray-600 divide-dashed">
          {moves.map((move: MoveSearchResult) => (
            <li key={move.id} className="px-4 py-2 hover:bg-stone-100">
              <a
                href={`/move/${move.id}`}
                className="block cursor-pointer"
                onClick={(e) => handleSelectMove(e, move)}
              >
                <div className="font-semibold">{move.name}</div>
                <div className="text-sm font-medium text-gray-600 space-x-3 flex items-center">
                  <div>
                    {move.service_date
                      ? formatDateWithOrdinal(parseISO(move.service_date))
                      : "No date set"}
                  </div>
                  <div>{move.mobile_phone}</div>
                  {/* <div>{move.alternate_phone}</div> */}
                  <div>{move.email}</div>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MoveSearch;
